import type { Ref } from 'vue'
import { nextTick } from 'vue'
import trackEvent from '@/track/trackEvent'

/**
 * 自定义@提及面板样式工具类
 * 用于管理el-mention下拉面板的样式和状态显示
 */

export interface MentionPanelState {
  mentionLoading: Ref<boolean>
  mentionLoadingMore: Ref<boolean>
  mentionHasMore: Ref<boolean>
  showEmptyState: Ref<boolean>
  mentionUsers: Ref<any[]>
  mentionCursor: Ref<string>
  sessionId: Ref<string>
}

export interface MentionSearchParams {
  currentPage: Ref<number>
  mentionSearchKeyword: Ref<string>
  $toast?: any
}

export interface MentionSelectParams {
  user: any
  selectedUsers?: Ref<Set<string>>
  $toast?: any
  pageName: string
  // 场景类型，用于确定定位方式
  scene?: 'post' | 'comment'
  isDuplicate?: boolean
}

/**
 * 添加loading状态和空态显示
 * @param state 面板状态对象
 */
export const addLoadingAndEmptyStates = (state: MentionPanelState) => {
  const dropdown = document.querySelector('.custom-mention-dropdown')
  if (!dropdown) return

  // 移除之前的状态元素
  const existingLoading = dropdown.querySelector('.mention-loading-overlay')
  const existingEmpty = dropdown.querySelector('.mention-empty-state')
  const existingLoadMore = dropdown.querySelector('.mention-load-more')
  const existingBottomOverlay = dropdown.querySelector('.mention-bottom-overlay')

  if (existingLoading) existingLoading.remove()
  if (existingEmpty) existingEmpty.remove()
  if (existingLoadMore) existingLoadMore.remove()
  if (existingBottomOverlay) existingBottomOverlay.remove()

  // 添加首次加载loading状态
  if (state.mentionLoading.value) {
    const loadingOverlay = document.createElement('div')
    loadingOverlay.className = 'mention-loading-overlay'
    loadingOverlay.innerHTML = `
      <div class="loading-content">
        <div class="loading-spinner">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
            <path d="M13.1496 15.9585C11.948 16.6519 10.5512 16.9295 9.17581 16.7482C7.80042 16.5668 6.52329 15.9368 5.54248 14.9556C4.56167 13.9745 3.93198 12.6972 3.75109 11.3217C3.57019 9.94629 3.84818 8.54959 4.54195 7.34823C5.23573 6.14687 6.30651 5.20799 7.58824 4.6772C8.86998 4.14641 10.291 4.05336 11.631 4.41249C12.971 4.77162 14.1551 5.56285 14.9996 6.66349C15.8441 7.76412 16.3019 9.11265 16.3019 10.4999C16.3019 11.0522 16.7496 11.4999 17.3019 11.4999C17.8541 11.4999 18.3019 11.0522 18.3019 10.4999C18.3019 8.6724 17.6988 6.89592 16.5863 5.446C15.4738 3.99609 13.914 2.95376 12.1488 2.48066C10.3835 2.00757 8.51151 2.13014 6.82302 2.82938C5.13454 3.52862 3.72395 4.76544 2.81001 6.34805C1.89607 7.93065 1.52986 9.77059 1.76816 11.5825C2.00647 13.3945 2.83597 15.0772 4.12804 16.3696C5.4201 17.6621 7.10253 18.4921 8.91439 18.731C10.7263 18.9699 12.5663 18.6042 14.1492 17.6908C14.6276 17.4148 14.7916 16.8032 14.5155 16.3249C14.2395 15.8465 13.6279 15.6825 13.1496 15.9585Z" fill="#101010"/>
          </svg>
        </div>
      </div>
    `
    loadingOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      border-radius: 12px;
    `

    // 添加loading spinner的样式
    const style = document.createElement('style')
    style.textContent = `
      .mention-loading-overlay .loading-spinner {
        width: 20px;
        height: 21px;
        animation: spin 1s linear infinite;
      }
      .mention-loading-overlay .loading-spinner svg {
        width: 100%;
        height: 100%;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(style)
    dropdown.appendChild(loadingOverlay)
  }

  // 添加翻页loading状态或没有更多提示 - 直接使用showEmptyState控制
  const hasRealUsers = !state.showEmptyState.value

  if (hasRealUsers) {
    const loadMore = document.createElement('div')
    loadMore.className = 'mention-load-more'

    if (state.mentionLoadingMore.value) {
      // 显示loading
      loadMore.innerHTML = `
        <div class="load-more-content">
          <div class="loading-spinner-small">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 21" fill="none">
              <path d="M13.1496 15.9585C11.948 16.6519 10.5512 16.9295 9.17581 16.7482C7.80042 16.5668 6.52329 15.9368 5.54248 14.9556C4.56167 13.9745 3.93198 12.6972 3.75109 11.3217C3.57019 9.94629 3.84818 8.54959 4.54195 7.34823C5.23573 6.14687 6.30651 5.20799 7.58824 4.6772C8.86998 4.14641 10.291 4.05336 11.631 4.41249C12.971 4.77162 14.1551 5.56285 14.9996 6.66349C15.8441 7.76412 16.3019 9.11265 16.3019 10.4999C16.3019 11.0522 16.7496 11.4999 17.3019 11.4999C17.8541 11.4999 18.3019 11.0522 18.3019 10.4999C18.3019 8.6724 17.6988 6.89592 16.5863 5.446C15.4738 3.99609 13.914 2.95376 12.1488 2.48066C10.3835 2.00757 8.51151 2.13014 6.82302 2.82938C5.13454 3.52862 3.72395 4.76544 2.81001 6.34805C1.89607 7.93065 1.52986 9.77059 1.76816 11.5825C2.00647 13.3945 2.83597 15.0772 4.12804 16.3696C5.4201 17.6621 7.10253 18.4921 8.91439 18.731C10.7263 18.9699 12.5663 18.6042 14.1492 17.6908C14.6276 17.4148 14.7916 16.8032 14.5155 16.3249C14.2395 15.8465 13.6279 15.6825 13.1496 15.9585Z" fill="#101010"/>
            </svg>
          </div>
          <span>加载更多...</span>
        </div>
      `
    } else if (!state.mentionHasMore.value) {
      // 显示没有更多
      loadMore.innerHTML = `
        <div class="load-more-content">
          <span>没有更多了</span>
        </div>
      `
    }

    if (loadMore.innerHTML) {
      // 隐藏加载更多
      loadMore.style.cssText = `
        padding: 6px;
        text-align: center;
        color: #999;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        display: none
      `

      // 找到用户列表容器并添加到底部
      const userList = dropdown.querySelector('.el-mention-dropdown__list')
      if (userList) {
        userList.appendChild(loadMore)
      } else {
        console.log('❌ 未找到用户列表容器:', dropdown.querySelectorAll('*'))
      }
    }
  }

  // 添加底部半透明遮罩（只有在hasMore为true时显示）
  const bottomOverlay = document.createElement('div')
  bottomOverlay.className = 'mention-bottom-overlay'
  bottomOverlay.style.cssText = `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
    pointer-events: none;
    z-index: 999;
    height: 28px;
    display: ${state.mentionHasMore.value ? 'block' : 'none'};
  `
  dropdown.appendChild(bottomOverlay)
}

/**
 * 更新底部蒙层显示状态
 * @param state 面板状态对象
 */
export const updateBottomOverlayVisibility = (state: MentionPanelState) => {
  const bottomOverlay = document.querySelector('.mention-bottom-overlay') as HTMLElement
  if (bottomOverlay) {
    const scrollContainer = document.querySelector(
      '.custom-mention-dropdown .el-scrollbar__wrap'
    ) as HTMLElement

    if (scrollContainer) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 30 // 距离底部30px内认为到底部

      // 显示蒙层的条件：
      // 只有在hasMore为false且滚动到底部时才隐藏蒙层
      // 其他所有情况都显示蒙层
      const shouldHideOverlay = !state.mentionHasMore.value && isAtBottom

      bottomOverlay.style.display = shouldHideOverlay ? 'none' : 'block'
    } else {
      // 如果找不到滚动容器，按原逻辑处理
      bottomOverlay.style.display = state.mentionHasMore.value ? 'block' : 'none'
    }
  }
}

/**
 * 搜索@提及用户API
 * @param query 搜索关键词
 * @param state 面板状态对象
 * @param params 搜索参数
 */
export const searchMentionUsers = async (
  query: string,
  state: MentionPanelState,
  params: MentionSearchParams
) => {
  // 翻页时显示翻页loading
  if (params.currentPage.value > 1) {
    state.mentionLoadingMore.value = true
  }

  try {
    const urlParams = new URLSearchParams({
      page: params.currentPage.value.toString(),
      limit: '20',
      query: query
    })

    // 如果是加载更多且有cursorCode，则添加cursorCode参数
    if (state.mentionCursor.value) {
      urlParams.append('cursor', state.mentionCursor.value)
    }

    // 如果有sessionId，则添加sessionId参数
    if (state.sessionId.value) {
      urlParams.append('sessionId', state.sessionId.value)
    }

    const response = await fetch(`/aigc/api/search/authors/mention?${urlParams.toString()}`)
    const res = await response.json()

    if (res.code === 200 && res.data) {
      const { dataList, cursor, hasMore, sessionId } = res.data

      // 更新分页状态
      state.mentionHasMore.value = hasMore || false
      state.mentionCursor.value = cursor?.after || ''

      // 保存sessionId
      if (sessionId) {
        state.sessionId.value = sessionId
      }

      if (dataList && dataList.length > 0) {
        const users = dataList.map((user: any) => {
          return {
            value: user.name,
            label: user.name,
            name: user.name,
            avatarUrl: user.avatarUrl, // 添加avatarUrl字段供模板使用
            verified:
              (user.tags && user.tags.includes('official')) ||
              (user.tags && user.tags.includes('author')),
            userId: user.uuid,
            tags: user.tags || []
          }
        })

        // 根据是否首次搜索决定替换还是追加
        if (params.currentPage.value === 1) {
          // 首次搜索时替换列表
          state.mentionUsers.value = users
        } else {
          // 翻页时追加到现有列表
          state.mentionUsers.value = [...state.mentionUsers.value, ...users]
        }

        state.showEmptyState.value = false
      } else {
        // dataList为空时显示空态
        if (params.currentPage.value === 1) {
          state.mentionUsers.value = []
          state.showEmptyState.value = true
        }
        state.mentionHasMore.value = false
      }
    } else {
      // 没有数据
      if (params.currentPage.value === 1) {
        state.mentionUsers.value = []
        state.showEmptyState.value = true
      }
      state.mentionHasMore.value = false
    }
  } catch (error) {
    if (params.currentPage.value === 1) {
      state.mentionUsers.value = []
      state.showEmptyState.value = true
    }
    state.mentionHasMore.value = false
  } finally {
    state.mentionLoading.value = false
    state.mentionLoadingMore.value = false
  }
}

/**
 * 处理@提及搜索
 * @param query 搜索关键词
 * @param state 面板状态对象
 * @param params 搜索参数
 * @param searchTimeout 搜索防抖定时器引用
 * @param loadMoreUsers 加载更多用户函数
 * @returns 搜索防抖定时器
 */
export const handleMentionSearch = async (
  query: string,
  state: MentionPanelState,
  params: MentionSearchParams,
  searchTimeout: Ref<NodeJS.Timeout | null>,
  loadMoreUsers?: () => Promise<void>
) => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }

  const timeout = setTimeout(
    async () => {
      params.mentionSearchKeyword.value = query
      // 重置状态
      state.mentionCursor.value = ''
      state.mentionHasMore.value = true
      params.currentPage.value = 1
      state.sessionId.value = '' // 重置sessionId

      // 设置loading状态
      state.mentionLoading.value = true
      state.showEmptyState.value = false

      // 立即显示loading状态
      addLoadingAndEmptyStates(state)

      // 执行搜索
      await searchMentionUsers(query, state, params)

      // 搜索完成后更新状态显示
      addLoadingAndEmptyStates(state)
      // 更新底部蒙层显示状态
      updateBottomOverlayVisibility(state)

      // 强制应用所有样式
      setTimeout(() => {
        // 添加滚动监听
        const scrollContainer = document.querySelector(
          '.custom-mention-dropdown .el-scrollbar__wrap'
        )
        if (scrollContainer && loadMoreUsers) {
          // 先移除之前的滚动监听器（如果存在）
          if ((scrollContainer as any)._mentionScrollHandler) {
            scrollContainer.removeEventListener(
              'scroll',
              (scrollContainer as any)._mentionScrollHandler
            )
            // 清理定时器
            if ((scrollContainer as any)._scrollTimeout) {
              clearTimeout((scrollContainer as any)._scrollTimeout)
            }
          }

          // 重置滚动位置到顶部
          scrollContainer.scrollTop = 0

          // 添加一个短暂延迟，避免重置滚动位置时触发scroll事件
          let scrollTimeout: NodeJS.Timeout | null = null

          const handleScroll = () => {
            // 清除之前的定时器
            if (scrollTimeout) {
              clearTimeout(scrollTimeout)
            }

            // 延迟执行，避免重置滚动位置时立即触发
            scrollTimeout = setTimeout(() => {
              const { scrollTop, scrollHeight, clientHeight } = scrollContainer as HTMLElement
              // 距离面板底部30px
              if (scrollHeight - scrollTop - clientHeight < 30) {
                loadMoreUsers()
              }

              // 滚动时更新蒙层显示状态
              updateBottomOverlayVisibility(state)
            }, 100) // 100ms延迟
          }

          // 保存监听器引用，以便后续移除
          ;(scrollContainer as any)._mentionScrollHandler = handleScroll
          ;(scrollContainer as any)._scrollTimeout = scrollTimeout
          scrollContainer.addEventListener('scroll', handleScroll)
        }
      }, 100) // 增加延迟，确保DOM完全渲染
    },
    query ? 300 : 0
  ) // 有查询内容时防抖300ms，空查询立即执行

  searchTimeout.value = timeout
  return timeout
}

/**
 * 处理@提及选择
 * @param params 选择参数
 */
export const handleMentionSelect = (params: MentionSelectParams) => {
  const userUuid = params.user.userId || params.user.uuid

  // 检查当前输入框中是否已经@过这个用户（使用唯一标识）
  if (params.isDuplicate) {
    if (params.$toast) {
      try {
        // 根据场景设置不同的定位配置
        const toastConfig: any = {
          message: '你已经@过ta了',
          type: 'text'
        }

        // 根据场景设置定位
        if (params.scene === 'post') {
          // 发帖区：相对于 detail-container 居中
          toastConfig.targetElement = '.checked-result'
          toastConfig.offset = { x: 0, y: 220 } // 垂直居中，水平居中
        } else if (params.scene === 'comment') {
          // 评论区：相对于 comments-textarea-box 向上偏移 64px
          toastConfig.targetElement = '.comments-textarea-box'
          toastConfig.offset = { x: 0, y: -64 }
        }
        // 如果不指定 scene，使用默认位置（屏幕顶部居中）

        params.$toast(toastConfig)
      } catch (error) {
        console.error('toast 调用失败:', error)
      }
    } else {
      console.log('$toast 不存在，无法显示提示')
    }
    return
  }

  // 添加埋点：@用户列表中选定用户
  trackEvent.track('click_mention', {
    page_name: params.pageName,
    to_user_id: userUuid
  })
}

/**
 * 计算mentionUserData - 公共逻辑
 * @param text 输入文本
 * @param selectedUsers 已选择的用户集合
 * @param userNameToUuidMap 用户名到UUID数组的映射
 * @param allUsers 所有用户数据（用于获取完整用户信息）
 * @returns mention用户数据数组
 */
export const computeMentionUserData = (
  text: string,
  selectedUsers: Ref<Set<string>>,
  userNameToUuidMap: Ref<Map<string, string[]>>,
  allUsers?: any[]
) => {
  console.log('🔍 computeMentionUserData 开始解析:', {
    text,
    selectedUsers: Array.from(selectedUsers.value),
    userNameToUuidMap: Object.fromEntries(userNameToUuidMap.value),
    allUsers: allUsers?.length || 0
  })

  const mentions: Array<{
    userId: string
    startIndex: number
    endIndex: number
    user?: any // 添加完整用户信息
  }> = []

  // 将selectedUsers转换为数组，保持用户选择的顺序
  const selectedUsersArray = Array.from(selectedUsers.value)

  // 为每个用户名维护一个已使用的UUID计数器
  const usedUuidsForUser = new Map<string, Set<string>>()

  // 查找所有@用户的位置 - 使用更精确的正则表达式
  const atRegex = /@([^\s@]+)/g
  let match

  // 重置正则表达式的lastIndex，确保从头开始匹配
  atRegex.lastIndex = 0

  while ((match = atRegex.exec(text)) !== null) {
    const startIndex = match.index
    const endIndex = match.index + match[0].length
    const userName = match[1] // 提取@后面的用户名
    const userUuids = userNameToUuidMap.value.get(userName) // 通过用户名获取UUID数组

    // 处理同名用户的情况
    if (userUuids && userUuids.length > 0) {
      let selectedUuid: string | null = null
      let selectedUser: any = null

      // 获取该用户名已使用的UUID集合
      if (!usedUuidsForUser.has(userName)) {
        usedUuidsForUser.set(userName, new Set<string>())
      }
      const usedUuids = usedUuidsForUser.get(userName)!

      // 按照用户选择的顺序来分配UUID
      for (const uniqueKey of selectedUsersArray) {
        const [keyUserName, keyUuid] = uniqueKey.split('#')

        // 只处理当前用户名的选择
        if (keyUserName === userName && !usedUuids.has(keyUuid)) {
          selectedUuid = keyUuid
          usedUuids.add(keyUuid) // 标记该UUID为已使用

          // 从allUsers中查找完整的用户信息
          if (allUsers) {
            selectedUser = allUsers.find(user => user.userId === keyUuid)
          }

          break
        }
      }

      // 如果用户没有明确选择过这个用户名的任何ID，则不处理
      if (!selectedUuid) {
        continue
      }

      if (selectedUuid) {
        mentions.push({
          userId: selectedUuid, // 使用UUID作为userId
          startIndex: startIndex,
          endIndex: endIndex,
          user: selectedUser // 添加完整用户信息
        })
      }
    }
  }

  return mentions
}

/**
 * 从唯一标识中提取UUID
 * @param uniqueKey 唯一标识，格式为 "用户名#UUID"
 * @returns UUID字符串
 */
export const extractUuidFromUniqueKey = (uniqueKey: string): string => {
  const parts = uniqueKey.split('#')
  return parts.length > 1 ? parts[1] : uniqueKey
}

/**
 * 创建用户唯一标识
 * @param userName 用户名
 * @param userUuid 用户UUID
 * @returns 唯一标识字符串
 */
export const createUserUniqueKey = (userName: string, userUuid: string): string => {
  return `${userName}#${userUuid}`
}

/**
 * 获取用户账号等级
 * @param tags 用户标签数组
 * @returns 账号等级字符串或null
 */
export const getAccountLevel = (tags: string[]): string | null => {
  if (!tags || tags.length === 0) return null

  if (tags.includes('official')) {
    return 'official'
  } else if (tags.includes('officialQualityCreator')) {
    return 'officialQualityCreator'
  }
  return null
}

/**
 * 翻页加载更多用户
 * @param state 面板状态对象
 * @param params 搜索参数
 */
export const loadMoreUsers = async (state: MentionPanelState, params: MentionSearchParams) => {
  if (state.mentionLoadingMore.value || !state.mentionHasMore.value) {
    return
  }

  params.currentPage.value += 1
  await searchMentionUsers(params.mentionSearchKeyword.value, state, params)

  // 等待Vue响应式更新完成，DOM重新渲染后再应用样式
  await nextTick()
  setTimeout(() => {
    addLoadingAndEmptyStates(state)
    // 更新底部蒙层显示状态
    updateBottomOverlayVisibility(state)
  }, 50) // 减少延迟时间，因为nextTick已经确保了DOM更新
}

/**
 * 同步selectedUsers与输入框内容
 * @param description 输入框内容
 * @param selectedUsers 已选择的用户集合
 * @param userNameToUuidMap 用户名到UUID的映射
 */
export const syncSelectedUsersWithContent = (
  description: Ref<string>,
  selectedUsers: Ref<Set<string>>,
  userNameToUuidMap: Ref<Map<string, string[]>>
) => {
  const text = description.value
  const currentMentionedUserKeysInText = new Set<string>()

  // 为每个用户名维护一个已使用的UUID计数器
  const usedUuidsForUser = new Map<string, Set<string>>()

  const atRegex = /@([^\s@]+)/g
  let match

  atRegex.lastIndex = 0

  while ((match = atRegex.exec(text)) !== null) {
    const userName = match[1]
    const userUuids = userNameToUuidMap.value.get(userName)

    if (userUuids && userUuids.length > 0) {
      let resolvedUniqueKey: string | null = null

      // 获取该用户名已使用的UUID集合
      if (!usedUuidsForUser.has(userName)) {
        usedUuidsForUser.set(userName, new Set<string>())
      }
      const usedUuids = usedUuidsForUser.get(userName)!

      // 1. 优先从已选择的用户中查找匹配的唯一标识，但跳过已使用的UUID
      for (const uuid of userUuids) {
        const uniqueKeyCandidate = createUserUniqueKey(userName, uuid)
        if (selectedUsers.value.has(uniqueKeyCandidate) && !usedUuids.has(uuid)) {
          resolvedUniqueKey = uniqueKeyCandidate
          usedUuids.add(uuid) // 标记该UUID为已使用
          break
        }
      }

      // 2. 如果没有在已选择的用户中找到，则从未使用的UUID中选择第一个
      if (!resolvedUniqueKey) {
        for (const uuid of userUuids) {
          if (!usedUuids.has(uuid)) {
            resolvedUniqueKey = createUserUniqueKey(userName, uuid)
            usedUuids.add(uuid) // 标记该UUID为已使用
            if (userUuids.length > 1) {
              console.warn(
                `⚠️ 同步时未找到精确匹配的同名用户 "${userName}"，选择未使用的UUID: ${uuid}`
              )
            }
            break
          }
        }
      }

      // 只有当resolvedUniqueKey不为null时才添加
      if (resolvedUniqueKey) {
        currentMentionedUserKeysInText.add(resolvedUniqueKey)
        console.log(
          `🔄 同步@用户: "${userName}" -> ${extractUuidFromUniqueKey(resolvedUniqueKey)} (唯一标识: ${resolvedUniqueKey})`
        )
      }
    }
  }

  // 不要清空selectedUsers，而是合并新的选择
  // 先移除不在文本中的用户
  const usersToRemove = new Set<string>()
  selectedUsers.value.forEach(uniqueKey => {
    const userName = uniqueKey.split('#')[0]
    if (!text.includes(`@${userName}`)) {
      usersToRemove.add(uniqueKey)
    }
  })

  // 移除不在文本中的用户
  usersToRemove.forEach(uniqueKey => {
    selectedUsers.value.delete(uniqueKey)
  })

  // 添加文本中的用户
  currentMentionedUserKeysInText.forEach(key => {
    selectedUsers.value.add(key)
  })
}
